import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useState } from 'react';
import { Stack } from 'syf-component-library/ui/spacing';
import { VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { useAccountSummary, useChangePaymentDueDateGet, useChangePaymentDueDatePut } from '../../../../const/syf-api-queries';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import SelectDueDate from '../../components/SelectDueDate';
import Disclaimer from '../../components/Disclaimer';
import Successful from '../../components/Successful';
import { ConfigContext } from '../../context';
import addOrdinalSuffix from '../../helpers/add-ordinal-suffix';
import { BodyContainer, ModalContentWrapper } from './subcomponents';
import { ChangePaymentDueDateScreen } from '../../typings';
import ChangePaymentDueDateErrors from './utils/ChangePaymentDueDateErrors';
import triggerChangePaymentDueDateAnalytics from '../../helpers/trigger-change-payment-due-date-analytics';
import { BUSINESS_ERROR_CODES } from '../../const/status-and-error-codes';
import { PAGE_NAME } from '../../const/analytics';
const ChangePaymentDueDateContent = () => {
    const { config, handleClose, selectedAccountId } = useContext(ConfigContext);
    const [selectedDay, setSelectedDay] = useState();
    const [errorDetails, setErrorDetails] = useState({});
    const [currentScreen, setCurrentScreen] = useState(ChangePaymentDueDateScreen.DEFAULT_SCREEN);
    const changeScreen = screen => setCurrentScreen(screen);
    const { data: accountSummaryData, isError: isAccountSummaryError, isFetching: isAccountSummaryFetching, error: getSummaryErrorResponse } = useAccountSummary(selectedAccountId);
    const { data: dueDateGetData, isFetching: isGetFetching, isError: isGetError, error: getErrorResponse } = useChangePaymentDueDateGet(selectedAccountId);
    const currentDueDay = dueDateGetData?.currentDueDay;
    const eligibleDueDays = dueDateGetData?.eligibleDueDays;
    const { data: putResponseData, mutate, isLoading: isPutLoading, isError: isPutError, error: putErrorResponse } = useChangePaymentDueDatePut(config);
    const date = accountSummaryData?.billing?.nextPaymentDate.split('-');
    const nextDueDay = date?.[2] ? Number(date[2]) : undefined;
    const currentDueDate = currentDueDay ?? nextDueDay;
    const hasEncounteredAPIError = isGetError || isPutError || isAccountSummaryError;
    const showLoader = isGetFetching || isPutLoading || isAccountSummaryFetching;
    const hasEncounteredError = Boolean(Object.keys(errorDetails).length > 0 && errorDetails?.hasError);
    const handleCloseModal = () => {
        triggerChangePaymentDueDateAnalytics({
            pageFunction: 'account services',
            pageKind: 'account services'
        });
        handleClose();
    };
    const handleSelectDay = (day) => {
        if (eligibleDueDays?.some(eligibleDay => eligibleDay.eligibleDueDay === day)) {
            if (selectedDay === day || currentDueDate === day)
                return;
            setSelectedDay(day);
        }
    };
    const handleClick = () => {
        if (selectedDay === undefined) {
            triggerChangePaymentDueDateAnalytics({
                pageName: PAGE_NAME.ERROR,
                errorMessage: 'Please select a new due date'
            });
            setCurrentScreen(ChangePaymentDueDateScreen.SELECT_DAY_ERROR_SCREEN);
            return;
        }
        const samplePayload = {
            'cipher.accountId': selectedAccountId,
            accountIdType: 'guid',
            newDueDay: selectedDay.toString()
        };
        mutate(samplePayload, {
            onSuccess: () => {
                changeScreen(ChangePaymentDueDateScreen.SUCCESSFUL_SCREEN);
            }
        });
    };
    const handleResponseError = useCallback(async (error) => {
        const responseStatus = error?.response?.status;
        const isBusinessError = responseStatus === 422;
        const parsedError = isBusinessError ? await error?.response?.json() : null;
        if (parsedError && isBusinessError) {
            const businessErrorCode = parsedError?.businessErrors?.[0].code;
            triggerChangePaymentDueDateAnalytics({
                pageName: PAGE_NAME.ERROR,
                errorMessage: businessErrorCode === BUSINESS_ERROR_CODES.DATE_ALREADY_CHANGED
                    ? 'You can change your due date once every 12 months'
                    : 'We cannot process your request for Change Due Date at this time.',
                pageSubFunction: 'non eligible'
            });
            setErrorDetails({ hasError: true, errorCode: businessErrorCode });
            return;
        }
        // 4xx, 5xx, or responseStatus is undefined
        if (!responseStatus || (responseStatus >= 400 && responseStatus < 600)) {
            triggerChangePaymentDueDateAnalytics({
                pageName: PAGE_NAME.ERROR,
                errorMessage: 'We’re having trouble retrieving your information.',
                pageSubFunction: 'technical'
            });
            setErrorDetails({ hasError: true, errorCode: '' });
        }
    }, []);
    useEffect(() => {
        if (hasEncounteredAPIError) {
            handleResponseError((getErrorResponse ||
                putErrorResponse ||
                getSummaryErrorResponse));
            changeScreen(ChangePaymentDueDateScreen.EXECUTION_ERROR_SCREEN);
        }
    }, [
        getErrorResponse,
        getSummaryErrorResponse,
        handleResponseError,
        putErrorResponse,
        hasEncounteredAPIError
    ]);
    useEffect(() => {
        // Resetting the screen to initial state when the API call is successful
        if (!showLoader &&
            !hasEncounteredAPIError &&
            currentScreen === ChangePaymentDueDateScreen.DEFAULT_SCREEN) {
            setCurrentScreen(ChangePaymentDueDateScreen.INITIAL_SCREEN);
        }
    }, [currentScreen, hasEncounteredAPIError, showLoader]);
    if (showLoader) {
        return (_jsx("div", { "data-testid": "skeleton-loader", children: _jsx(VerticalCardLoader, {}) }));
    }
    const showSelectDueDate = !hasEncounteredError &&
        !showLoader &&
        currentScreen !== ChangePaymentDueDateScreen.EXECUTION_ERROR_SCREEN &&
        [
            ChangePaymentDueDateScreen.INITIAL_SCREEN,
            ChangePaymentDueDateScreen.SELECT_DAY_ERROR_SCREEN
        ].includes(currentScreen);
    return (_jsxs(ModalContentWrapper, { id: "modal-content-wrapper", children: [_jsx(BodyContainer, { "data-cy": "change-payment-due-date-container", children: hasEncounteredError ? (_jsx(ChangePaymentDueDateErrors, { errorCode: errorDetails?.errorCode })) : (_jsxs(_Fragment, { children: [showSelectDueDate && (_jsxs(_Fragment, { children: [_jsx(Header, { date: addOrdinalSuffix(currentDueDate), currentScreen: currentScreen }), _jsx(SelectDueDate, { currentDueDay: currentDueDate, eligibleDueDays: eligibleDueDays, handleSelectDay: handleSelectDay, selectedDay: selectedDay }), _jsx(Stack, { size: "base" }), _jsx(Disclaimer, {})] })), currentScreen === ChangePaymentDueDateScreen.SUCCESSFUL_SCREEN && (_jsx(Successful, { currentDueDay: currentDueDate, selectedDay: selectedDay, isPromoExtended: Boolean(putResponseData?.promosExtended) }))] })) }), _jsx(Footer, { handleClose: handleCloseModal, handleClick: handleClick, currentScreen: currentScreen, selectedDay: selectedDay })] }));
};
export default ChangePaymentDueDateContent;
